/**
 * Navigation System
 * Data-driven navigation for NeutralAiz platform
 *
 * @module lib/navigation
 *
 * @example
 * // In your layout
 * import { NavigationProvider } from '@/lib/navigation';
 *
 * function Layout({ children }) {
 *   return (
 *     <NavigationProvider userId={userId} organizationId={orgId}>
 *       {children}
 *     </NavigationProvider>
 *   );
 * }
 *
 * // In components
 * import { useSidebarNavigation, useMainNavigation } from '@/lib/navigation';
 *
 * function Sidebar() {
 *   const { groups, expandedGroups, toggleGroup } = useSidebarNavigation();
 *   // ...
 * }
 */

// ============================================================================
// Types
// ============================================================================

export type {
  // Enums
  NavPosition,
  NavColorScheme,
  NavAuthRequirement,
  BadgeType,

  // Database types
  DbNavigationGroup,
  DbNavigationItem,
  OrgNavigationOverride,

  // Resolved types
  NavBadge,
  ResolvedNavItem,
  ResolvedNavGroup,
  NavigationStructure,

  // Context types
  NavigationContextValue,

  // API types
  NavigationApiResponse,
  NavigationCache,
  NavigationPreferences,

  // Extended API types
  GetUserNavigationParams,
  GetUserNavigationResponse,
  ListNavigationItemsParams,
  ListNavigationItemsResponse,
  CreateNavigationItemParams,
  CreateNavigationItemResponse,
  UpdateNavigationItemParams,
  UpdateNavigationItemResponse,
  ListNavigationGroupsParams,
  ListNavigationGroupsResponse,
  CreateNavigationGroupParams,
  CreateNavigationGroupResponse,
  GetNavigationOverridesParams,
  GetNavigationOverridesResponse,
  BulkUpdateNavigationOverridesParams,
  BulkUpdateNavigationOverridesResponse,
  NavigationUserContext,
} from './types';

// ============================================================================
// Icons
// ============================================================================

export {
  resolveIcon,
  isValidIconName,
  getAvailableIcons,
  searchIcons,
  getCommonIcons,
  getIconCategories,
  clearIconCache,
  type IconCategory,
} from './icons';

// ============================================================================
// Hooks
// ============================================================================

export {
  // Core hook
  useNavigation,
  useNavigationOptional,

  // Position-specific hooks
  useSidebarNavigation,
  useMainNavigation,
  useMobileNavigation,
  useUserMenuNavigation,
  useFooterNavigation,

  // Utility hooks
  useActiveNavItem,
  useNavBreadcrumbs,
  useNavItemByKey,
  useNavItemsByGroup,
  useNavGroupByKey,
  useIsNavPathActive,
  useSimplifiedNavigation,
  useNavSearch,
  useNavRefresh,
} from './hooks';

export { useSubmenuSections } from './useSubmenuSections';

// ============================================================================
// Provider
// ============================================================================

export { NavigationProvider, NavigationContext } from './NavigationProvider';
export { NavigationProviderWrapper } from './NavigationProviderWrapper';
export { default } from './NavigationProvider';

